import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { X, Plus, Minus, ShoppingCart, Trash2, ArrowRight, Truck } from 'lucide-react';
import { categories } from '../../data/mock';

const CartDrawer = () => {
  const {
    isCartOpen,
    closeCart,
    cartItems,
    cartTotal,
    updateQuantity,
    removeFromCart
  } = useCart();

  if (!isCartOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-dark-900/50 backdrop-blur-sm z-50"
        onClick={closeCart}
      />

      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white z-50 shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-dark-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-athletic-50 text-athletic-700 rounded-full flex items-center justify-center">
              <ShoppingCart className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg text-dark-900 font-semibold">Your cart</h2>
              <p className="text-sm text-dark-400">{cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}</p>
            </div>
          </div>
          <button
            onClick={closeCart}
            className="p-2 hover:bg-dark-50 rounded-full transition-colors text-dark-400 hover:text-dark-700"
            aria-label="Close cart"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-5 bg-dark-50/60">
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-20 h-20 bg-athletic-50 rounded-2xl flex items-center justify-center mb-5">
                <ShoppingCart className="w-9 h-9 text-athletic-300" />
              </div>
              <h3 className="font-semibold text-dark-900 text-lg mb-2">Your cart is empty</h3>
              <p className="text-dark-500 text-sm mb-6">Add something you love to get started</p>
              <button
                onClick={closeCart}
                className="px-6 py-3 bg-athletic-600 hover:bg-athletic-700 text-white font-medium rounded-full transition-all"
              >
                Browse products
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {cartItems.map(item => {
                const itemCategory = categories.find(c => c.id === item.categoryId);
                return (
                  <div key={item.id} className="flex gap-3 p-4 bg-white rounded-2xl border border-dark-100">
                    <div className="w-20 h-20 bg-dark-50 rounded-xl overflow-hidden flex-shrink-0">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] text-athletic-600 uppercase tracking-wider font-medium mb-0.5">
                        {itemCategory?.name || 'Tech'}
                      </p>
                      <h4 className="font-medium text-dark-900 text-sm mb-1 line-clamp-2">
                        {item.name}
                      </h4>
                      <p className="text-dark-900 font-semibold text-sm">
                        ${item.price.toFixed(2)}
                      </p>
                      <div className="flex items-center justify-between mt-2.5">
                        <div className="flex items-center border border-dark-200 rounded-full overflow-hidden">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="p-1.5 hover:bg-dark-50 transition-colors text-dark-600"
                            aria-label="Decrease"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="w-8 text-center text-sm font-semibold text-dark-900">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="p-1.5 hover:bg-dark-50 transition-colors text-dark-600"
                            aria-label="Increase"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="p-1.5 text-dark-300 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                          aria-label="Remove item"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="p-5 border-t border-dark-100 space-y-3 bg-white">
            <div className="flex items-center gap-2 px-4 py-2.5 bg-athletic-50 border border-athletic-100 rounded-xl">
              <Truck className="w-4 h-4 text-athletic-600" />
              <span className="text-athletic-700 text-sm font-medium">Free USA shipping included</span>
            </div>
            <div className="flex items-center justify-between pt-1">
              <span className="text-dark-600 font-medium">Subtotal</span>
              <span className="text-2xl font-semibold text-dark-900">${cartTotal.toFixed(2)}</span>
            </div>
            <Link
              to="/checkout"
              onClick={closeCart}
              className="flex items-center justify-center gap-2 w-full py-3.5 bg-athletic-600 hover:bg-athletic-700 text-white font-semibold rounded-full transition-all"
            >
              Checkout
              <ArrowRight className="w-5 h-5" />
            </Link>
            <button
              onClick={closeCart}
              className="w-full text-center text-dark-500 hover:text-athletic-700 py-1.5 text-sm font-medium transition-colors"
            >
              Continue shopping
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
